// The SKETCH tool: the five draw/erase shapes (voxel paint, extrude, circle,
// freeform lasso, box), routed by uiState.shapeMode with uiState.actionMode
// picking draw vs erase. Owns the trigger-press start for all five and the
// freeform commit (close the lasso → fill → select the result). The other
// shapes need no commit work — their gestures finalize through the generic
// idle/endTransaction tail in main.
//
// Per-tap extrude depth=1 means: DRAW adds one layer to the entire
// flood-filled face region; ERASE deletes the entire face region. To extrude
// further, hold the trigger and drag along the face normal.

import type * as THREE from "three";
import { uiState } from "../ui/state";
import { type PickTarget, type CellBounds } from "../interaction/pick";
import { type MirrorConfig, setMirror } from "../interaction/mirror";
import { startVoxelPaint } from "../interaction/handlers/voxel-paint";
import { startExtrude, commitExtrude } from "../interaction/handlers/extrude";
import { startCircle, commitCircle } from "../interaction/handlers/circle";
import {
  startBox,
  commitBoxDraw,
  commitBoxErase,
} from "../interaction/handlers/box";
import { interaction } from "../interaction/state";
import { type VoxelGrid } from "../model/voxel-grid";
import { selectionKeySet } from "../core/edit-core/selection";
import { type ToolRegistry } from "../app/tool-registry";

export interface SketchToolDeps {
  tools: ToolRegistry;
  pickFor: (i: number) => PickTarget | null; // the controller's last-frame pick
  // Resolve which scene object this gesture draws into (a voxel hit → that
  // object; a room-surface hit → a room-aligned object, created on demand) and
  // mark it as the gesture's target — main routes the per-frame drag handlers
  // and the commit at that object until release.
  resolveGestureTarget: (i: number) => { grid: VoxelGrid; group: THREE.Object3D };
  // The LOCKED gesture target's grid (set at start, stable until release) — the
  // box_draw commit fills into this, not a re-resolved pick (the cursor may have
  // wandered onto another object mid-drag).
  gestureGrid: () => VoxelGrid;
  cellBounds: CellBounds; // live work-volume bounds (mutated in place by resize)
  // The RAW mirror config (per-axis booleans) — setMirror treats "no axis on"
  // as off, so sketch arms it unconditionally each gesture.
  mirrorConfig: () => MirrorConfig;
  screenDriven: () => boolean; // desktop drives extrude depth from the screen ray
}

export function installSketchTool(deps: SketchToolDeps): void {
  deps.tools.register({
    id: "sketch",
    claims: () => uiState.topMode === "sketch",
    start: (i) => {
      const pick = deps.pickFor(i);
      if (!pick) return;
      const { grid: tGrid, group: tGroup } = deps.resolveGestureTarget(i);
      const action = uiState.actionMode;
      const shape = uiState.shapeMode;

      // Arm mirror drawing for this gesture. setMirror treats "no axis on" as off.
      setMirror(deps.mirrorConfig());

      if (shape === "voxel") {
        startVoxelPaint(pick, i, action, tGrid, deps.cellBounds, uiState.currentPaletteIndex);
      } else if (shape === "extrude") {
        if (pick.kind === "voxel") {
          // Desktop drives depth via the ray-to-normal-axis closest point (the
          // mouse pointer can't translate); VR uses the laser endpoint.
          startExtrude(
            pick,
            i,
            action,
            tGrid,
            tGroup,
            deps.cellBounds,
            deps.screenDriven(),
            selectionKeySet(), // selected cells are masked out of the extrude
          );
        }
        // EXTRUDE on a bare surface is a no-op for both DRAW and ERASE —
        // there's no face to extrude from. Single-cell placement is the
        // VOXEL shape's job; users select that mode explicitly when they
        // want it.
      } else if (shape === "circle") {
        startCircle(pick, i, action, tGrid, deps.cellBounds, uiState.currentPaletteIndex);
      } else if (shape === "freeform") {
        // Freeform is a desktop multi-click polygon lasso built OUTSIDE the
        // press-drag gesture seam (see src/app/desktop-input.ts +
        // interaction/handlers/freeform.ts). No gesture starts here — returning
        // keeps it from falling through to BOX.
        return;
      } else {
        // BOX
        startBox(pick, i, action, tGrid, deps.cellBounds, uiState.currentPaletteIndex);
      }
    },
    // box_draw / box_erase preview during the drag (no grid writes); the real
    // fill / carve lands here, once, on release — in the open transaction.
    // extrude_* writes live during the drag (suppressed history) and records its
    // one snapshot undo step here on release.
    kinds: [
      "box_draw", "box_erase",
      "extrude_draw", "extrude_erase", "extrude_recolor",
      "circle_draw", "circle_erase", "circle_recolor",
    ],
    commit: (i) => {
      if (interaction.kind === "box_draw") {
        if (interaction.controllerIndex !== i) return; // owned but declined
        commitBoxDraw(deps.gestureGrid(), deps.cellBounds, uiState.currentPaletteIndex);
      } else if (interaction.kind === "box_erase") {
        if (interaction.controllerIndex !== i) return;
        commitBoxErase(deps.gestureGrid(), deps.cellBounds);
      } else if (
        interaction.kind === "extrude_draw" ||
        interaction.kind === "extrude_erase" ||
        interaction.kind === "extrude_recolor"
      ) {
        if (interaction.controllerIndex !== i) return;
        commitExtrude(deps.gestureGrid(), deps.cellBounds);
      } else if (
        interaction.kind === "circle_draw" ||
        interaction.kind === "circle_erase" ||
        interaction.kind === "circle_recolor"
      ) {
        if (interaction.controllerIndex !== i) return;
        commitCircle();
      }
    },
  });
}
